import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchCatalog } from '../../services/catalog/catalogService.js';
import { indexGoogleAdSuitability } from '../../services/ads/googleAdSuitability.js';

const CatalogContext = createContext(null);
const CATALOG_REFRESH_MS = 60_000;
const emptyCatalog = {
  titles: [],
  rails: [],
  movies: [],
  shows: [],
  originals: [],
  shorts: [],
  featured: [],
  programming: {
    sourceOfTruth: '',
    frontendContractVersion: 0,
    publishedRevision: '',
    featuredIds: [],
    originalIds: [],
    heroManagers: {},
    heroes: { Home: [], Movies: [], Shows: [], Originals: [] },
  },
};

function applyCatalog(nextCatalog) {
  indexGoogleAdSuitability(nextCatalog);
  return nextCatalog;
}

export function CatalogProvider({ children }) {
  const [catalog, setCatalog] = useState(emptyCatalog);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const nextCatalog = applyCatalog(await fetchCatalog());
      setCatalog(nextCatalog);
      setStatus('ready');
    } catch (nextError) {
      setError(nextError);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    let active = true;
    let refreshing = false;
    let controller = null;

    const commitCatalog = (nextCatalog) => {
      const prepared = applyCatalog(nextCatalog);
      setCatalog((current) => {
        const currentRevision = String(current?.programming?.publishedRevision || '');
        const nextRevision = String(prepared?.programming?.publishedRevision || '');
        if (nextRevision && currentRevision === nextRevision) return current;
        return prepared;
      });
      setError(null);
      setStatus('ready');
    };

    const refresh = async ({ foreground = false } = {}) => {
      if (!active || refreshing) return;
      refreshing = true;
      controller = new AbortController();
      if (foreground) {
        setStatus('loading');
        setError(null);
      }
      try {
        const nextCatalog = await fetchCatalog({ attempts: foreground ? 3 : 1, signal: controller.signal });
        if (active) commitCatalog(nextCatalog);
      } catch (nextError) {
        if (!active || nextError?.name === 'AbortError') return;
        if (foreground) {
          setError(nextError);
          setStatus('error');
        } else {
          console.warn('[WatchVIM catalog] background refresh failed', nextError);
        }
      } finally {
        refreshing = false;
      }
    };

    const refreshVisibleCatalog = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    refresh({ foreground: true });
    const interval = window.setInterval(refreshVisibleCatalog, CATALOG_REFRESH_MS);
    window.addEventListener('focus', refreshVisibleCatalog);
    document.addEventListener('visibilitychange', refreshVisibleCatalog);

    return () => {
      active = false;
      controller?.abort();
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshVisibleCatalog);
      document.removeEventListener('visibilitychange', refreshVisibleCatalog);
    };
  }, []);

  const value = useMemo(() => ({ catalog, status, error, retry: load }), [catalog, status, error, load]);
  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
  const value = useContext(CatalogContext);
  if (!value) throw new Error('useCatalog must be used within CatalogProvider');
  return value;
}
